<script setup>
import { ref } from 'vue'

defineProps({
  tabs: {
    type: Array,
    required: true,
  },
})
const tabIndex = ref(0)

function enter (el) {
  el.style.width = '0px'
  requestAnimationFrame(() => {
    el.style.width = el.scrollWidth + 'px'
  })
}
function afterEnter (el) {
  el.style.width = 'auto'
}
function leave (el) {
  el.style.width = el.scrollWidth + 'px'
  requestAnimationFrame(() => {
    el.style.width = '0px'
  })
}
</script>

<!-- TODO handle overflow "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" -->

<template>
  <div>
    <div class="fixed top-0 left-0 px-4 pt-4 w-full bg-white z-[100]">
      <div class="flex text-neutral-gray-700 text-sm w-full min-w-0 gap-2">
        <div
          v-for="(tab, index) in tabs"
          :key="index"
          class="pb-2 tab transition relative cursor-pointer"
          :class="{ 'text-neutral-gray-450': tab.disabled }"
          :aria-selected="tabIndex === index"
          @click="tabIndex = index"
        >
          <span
            v-if="tab.name"
            class="flex justify-center truncate items-center transition rounded-lg p-2 gap-1"
            :class="{ 'bg-microsoft-azure': tabIndex === index, 'bg-gray-100 hover:bg-gray-200': tabIndex !== index }"
          >
            <component
              :is="tab.icon"
              v-if="tab.icon"
              class="w-5 h-5 shrink-0"
            />
            <transition
              @enter="enter"
              @after-enter="afterEnter"
              @leave="leave"
            >
              <span
                v-if="tabIndex === index"
                class="truncate overflow-hidden transition-all duration-100 ease-in-out"
              >
                {{ tab.name }}
              </span>
            </transition>
            <span
              v-if="tab.badge"
              class="rounded text-white px-1 ml-1 min-w-[1.2rem] text-center text-[10px] font-semibold align-baseline"
              :class="{
                'bg-microsoft-blue-default': tab.badge.type === 'info',
                'bg-amber-500': tab.badge.type === 'warning',
              }"
            >
              {{ tab.badge.text }}
            </span>
          </span>
          <slot
            v-if="!tab.name"
            :name="`${tab.name}-tab`"
          />
        </div>
      </div>
      <div class="divider w-full" />
    </div>
    <div class="mt-16">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
      >
        <slot
          v-if="tabIndex === index"
          :name="tab.slot"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.tab {
  &:after {
    opacity: 0;
    content: '';
    @apply transition-all duration-200 ease-in;
    @apply bg-microsoft-blue-default rounded absolute;
    @apply left-0 bottom-[-2px];
    animation: fadeIn 0.2s ease-in;
  }

  &[aria-selected="true"] {
    @apply font-bold;

    &:after {
      opacity: 1;
      @apply h-[3px] w-full;
    }
  }
}
</style>
